import { trans } from 'sharedKernel/i18n'
import { DEFAULT_OPPORTUNITY_IMAGE } from './FormWrapper/list/constants'
import type { ScoutingOpportunity } from './api/scoutingService'
import {
  OPPORTUNITY_VERTICAL_OPTIONS,
} from './OpportunityForm/constants'
import {
  getOpportunityCoverPathFromFiles,
  getOpportunityFiles,
  isValidOpportunityCoverPath,
} from './opportunityFiles'

export type { ScoutingOpportunityFile } from './opportunityFiles'
export {
  getOpportunityCoverPathFromFiles,
  getOpportunityFiles,
  getOpportunitySupportFileDisplayName,
  getOpportunitySupportFiles,
  isOpportunityCoverImageFile,
  isOpportunitySupportFile,
  normalizeOpportunityFile,
} from './opportunityFiles'

type VerticalOption = {
  id: string
  label?: string
  labelKey?: string
}

const getVerticalOptionLabel = (option: VerticalOption): string => {
  if (option.labelKey) {
    return trans(option.labelKey)
  }
  return option.label ?? option.id
}

export const displayOrDash = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') return '-'
  return String(value)
}

export const formatOpportunityVerticalsDisplay = (
  opportunity: Pick<ScoutingOpportunity, 'operationArea' | 'verticals' | 'otherVertical'> | null | undefined
): string => {
  if (!opportunity) return ''

  if (opportunity.operationArea?.trim()) {
    return opportunity.operationArea.trim()
  }

  if (!opportunity.verticals?.length) {
    return ''
  }

  return opportunity.verticals
    .map(vertical => {
      const normalized = vertical.toLowerCase()
      if (normalized === 'other') {
        return opportunity.otherVertical?.trim() || trans('scouting.opportunity.vertical.other')
      }

      const option = OPPORTUNITY_VERTICAL_OPTIONS.find(item => item.id === vertical)
      return option ? getVerticalOptionLabel(option) : vertical
    })
    .filter(Boolean)
    .join(', ')
}

const readCoverPath = (source: Record<string, unknown> | null | undefined): string | undefined => {
  if (!source) return undefined

  const style = source.style as Record<string, unknown> | undefined
  const candidates = [
    source.coverImagePath,
    style?.coverImagePath,
    source.imagePath,
    source.image,
    source.coverImage,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && isValidOpportunityCoverPath(candidate)) {
      return candidate
    }
  }

  return undefined
}

export const getOpportunityCoverImage = (opportunity: ScoutingOpportunity | null | undefined): string => {
  if (!opportunity) return DEFAULT_OPPORTUNITY_IMAGE

  const fromFiles = getOpportunityCoverPathFromFiles(getOpportunityFiles(opportunity))
  if (fromFiles) return fromFiles

  const directPath = readCoverPath(opportunity as unknown as Record<string, unknown>)
  if (directPath) return directPath

  const nestedData = (opportunity as ScoutingOpportunity & { data?: Record<string, unknown> }).data
  const nestedPath = readCoverPath(nestedData)
  if (nestedPath) return nestedPath

  return DEFAULT_OPPORTUNITY_IMAGE
}

export const isExternalUrl = (value: string): boolean => /^https?:\/\//i.test(value.trim())

export const normalizeExternalUrl = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return isExternalUrl(trimmed) ? trimmed : `https://${trimmed}`
}

export const isInterestedStartup = (startup: Record<string, unknown>): boolean => {
  if (startup.demonstratedInterest === true) return true
  if (startup.hasDemonstratedInterest === true) return true
  if (startup.interestDemonstrated === true) return true
  if (startup.isInterested === true) return true
  if (startup.interestDemonstrationId != null && startup.interestDemonstrationId !== '') return true
  return false
}
